import { defineComponent as _defineComponent } from 'vue'
import { toDataModel, updateModel, setState, getChildModel } from 'ah-common-lib/src/form/helpers';
import { FormDefinition, FormEvent, FormValidation } from 'ah-common-lib/src/form/interfaces';
import { useSettingsStore } from '@/app/store/settingsModule';
import { residenceFM } from '@/app/helpers/registration/forms';
import { computed, onBeforeMount, reactive, watch, PropType } from 'vue';
import { BaseRegistrationModel, ClientType, CompanyRegistrationModel } from 'ah-api-gateways';
import { useRequestManager } from 'ah-common-lib/src/requestManager/useRequestManager';
import { makeFormModel } from 'ah-common-lib/src/form/helpers';
import { emailField, selectField, textField } from 'ah-common-lib/src/form/models';
import { requiredIfStateValue, validName } from 'ah-common-lib/src/form/validators';


export default /*#__PURE__*/_defineComponent({
  __name: 'CountryOfIncorporation',
  props: {
  model: {
    type: Object as PropType<BaseRegistrationModel | CompanyRegistrationModel>,
    default: null,
  },
  clientType: {
    type: String as PropType<ClientType>,
    required: true,
  },
  heading: {
    type: String,
    required: true,
  },
},
  emits: ["update:validation", "update:model", "proceed"],
  setup(__props, { emit }: { emit: ({
  (e: 'update:validation', value: FormValidation<BaseRegistrationModel> | null): void;
  (e: 'update:model', value: BaseRegistrationModel): void;
  (e: 'proceed', value: void): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props;

const activeCountries = [
  { value: 'GB', label: 'United Kingdom' },
  { value: 'IE', label: 'Ireland' },
  { value: 'BE', label: 'Belgium' },
  { value: 'NL', label: 'Netherlands' },
  { value: 'LU', label: 'Luxembourg' },
  { value: 'CH', label: 'Switzerland' },
  { value: 'EE', label: 'Estonia' },
  { value: 'PT', label: 'Portugal' },
  { value: 'notListed', label: 'My country is not listed' },
];





function makeRequestToBeNotifiedFM(clientType: boolean, countries: { value: string; label: string }[]) {
  return makeFormModel({
    name: 'requestedToBeNotifiedForm',
    fieldType: 'form',
    fields: [
      selectField('countryCode', 'Country', countries, {
        required: true,
        placeholder: 'Select Country',
      }),
      textField(
        'firstName',
        'Name',
        { fieldWrapperClass: 'col col-6', placeholder: 'Name' },
        { required: requiredIfStateValue('firstName'), validName }
      ),
      textField(
        'lastName',
        'Surname',
        { fieldWrapperClass: 'col col-6', placeholder: 'Surname' },
        { required: requiredIfStateValue('lastName'), validName }
      ),

      ...(clientType ? [textField('companyName', 'Company Name', { required: true })] : []),
      emailField('email', 'Email address', {
        maxLength: 100,
      }),
    ],
  });
}

const requestManager = useRequestManager().manager;

const settingsStore = useSettingsStore();

const countryFormDef = reactive<FormDefinition>({
  form: residenceFM(),
  validation: null,
});

const requestToBeNotifiedFormDef = reactive<FormDefinition>({
  form: makeRequestToBeNotifiedFM(props.clientType === ClientType.COMPANY, []),
  validation: null,
});

const showRequestToBeNotifiedForm = computed(() => {
  // Only show if 'My country is not listed' is selected
  return countryFormDef.form.countryCode === 'notListed';
});

const shouldDisplayReverseSolicitation = computed(
  () => countryFormDef.form.countryCode && countryFormDef.form.countryCode !== 'GB'
);

const calculatedModel = computed(() => {
  const { reverseSolicitation, ...address } = toDataModel(countryFormDef.form);
  return {
    ...props.model,
    address: {
      ...props.model?.address,
      ...address,
    },
    reverseSolicitation,
  } as BaseRegistrationModel;
});

const validation = computed(() => {
  return {
    $model: calculatedModel.value,
    $invalid: !!countryFormDef.validation?.$invalid || !!requestToBeNotifiedFormDef.validation?.$invalid,
    $dirty: !!countryFormDef.validation?.$dirty || !!requestToBeNotifiedFormDef.validation?.$dirty,
  } as FormValidation<BaseRegistrationModel>;
});

function onCountryFormEvent(event: FormEvent) {
  if (event.event === 'form-field-set-value') {
    emit('update:model', calculatedModel.value);
  }
}

onBeforeMount(() => {
  const countryCodeField = getChildModel(countryFormDef.form, 'countryCode')!;

  // Temporary added hardcoded list of countries required for account creation.
  setState(countryCodeField, 'options', activeCountries);
});

onBeforeMount(() => {
  const countryCodeField = getChildModel(requestToBeNotifiedFormDef.form, 'countryCode')!;

  settingsStore.loadCountries(true).then(() => {
    const activeCountriesCode = activeCountries.map((country) => country.value);

    const countries = settingsStore.allCountries
      .filter((country) => !activeCountriesCode.includes(country.cc))
      .map((country) => ({
        value: country.cc,
        label: country.name,
      }));

    setState(countryCodeField, 'options', countries);
  });
});

watch(
  () => props.clientType,
  (newClientType) => {
    requestToBeNotifiedFormDef.form = makeRequestToBeNotifiedFM(newClientType === ClientType.COMPANY, []);
  }
);

watch(
  () => countryFormDef.form.countryCode,
  (val) => {
    const field = getChildModel(countryFormDef.form, 'reverseSolicitation')!;
    setState(field, 'hidden', !shouldDisplayReverseSolicitation.value);
    setState(field, 'required', shouldDisplayReverseSolicitation.value);
    if (val !== 'notListed') {
      requestToBeNotifiedFormDef.validation = null;
    }
  },
  { immediate: true }
);

watch(
  validation,
  () => {
    emit('update:validation', validation.value);
  },
  { immediate: true }
);

watch(
  () => props.model,
  () => {
    updateModel(countryFormDef.form, {
      ...props.model?.address,
      reverseSolicitation: props.model?.reverseSolicitation,
    });
  },
  { immediate: true }
);

return { __sfc: true,activeCountries, props, emit, makeRequestToBeNotifiedFM, requestManager, settingsStore, countryFormDef, requestToBeNotifiedFormDef, showRequestToBeNotifiedForm, shouldDisplayReverseSolicitation, calculatedModel, validation, onCountryFormEvent }
}

})